import React, { useLayoutEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import useUserStore from 'store/useUserStore';
import { openErrorToaster } from 'services/toast.service';
import {
  getDeniedRedirect,
  getRuleById,
  resolveAccessibleLandingPath,
} from './routeAccess';
import { usePermissions } from './usePermission';

const ROUTE_DENIED_TOAST_MS = 10000;

/**
 * Enforces route-level access from the central config (ruleId).
 * Checks can(rule.readPermission) via usePermissions() so that per-user
 * permission overrides and role defaults are both respected.
 *
 * This improves UX and app integrity; privileged APIs must still authorize on the server.
 */
export type RouteAccessGuardProps = {
  ruleId: string;
  children: React.ReactNode;
  /** Override redirect when access is denied */
  redirectTo?: string;
};

export default function RouteAccessGuard({
  ruleId,
  children,
  redirectTo,
}: RouteAccessGuardProps): React.ReactNode {
  const user = useUserStore(s => s.user);
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const rule = getRuleById(ruleId);
  const deniedRedirect = resolveAccessibleLandingPath(can, [
    ...(redirectTo ? [redirectTo] : []),
    getDeniedRedirect(rule),
  ]);
  const mayAccess = can(rule.readPermission);
  const denyToastGuardRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!user || mayAccess) {
      denyToastGuardRef.current = null;
      return;
    }
    const dedupeKey = `${ruleId}:${location.pathname}`;
    if (denyToastGuardRef.current === dedupeKey) {
      return;
    }
    denyToastGuardRef.current = dedupeKey;

    const toastStorageKey = `rosie_route_403_toast:${dedupeKey}`;
    let showToast = true;
    try {
      const prev = sessionStorage.getItem(toastStorageKey);
      if (prev && Date.now() - Number(prev) < 2000) {
        showToast = false;
      } else {
        sessionStorage.setItem(toastStorageKey, String(Date.now()));
      }
    } catch {
      // ignore quota / private mode
    }

    if (showToast) {
      openErrorToaster(
        'You do not have permission to access this page. (403)',
        ROUTE_DENIED_TOAST_MS,
        'Access denied',
      );
    }
    const safeRedirect = (p: string) =>
      p.startsWith('/') && !p.includes('://') ? p : '/';
    navigate(safeRedirect(deniedRedirect), { replace: true });
  }, [
    user,
    mayAccess,
    deniedRedirect,
    navigate,
    ruleId,
    location.pathname,
  ]);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!mayAccess) {
    return null;
  }

  return children;
}
